<script setup lang="ts">
import type Button from '@/components/auth/verification/Box/Proceed.vue';
import type TimeLimit from '@/components/user/account/Protection/TimeLimit.vue';
import type LossLimit from '@/components/user/account/Protection/LostLimit.vue';
import type {
  UserLimit,
  UserLimitTime,
  UserPendingLimits,
} from '@/types/UserLimits';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isCaptchaRegisterEnabled } = useCaptcha();
const { fetchUserProfile } = useUserData();

const isLoading = ref(false);

const timeLimitRef = ref<InstanceType<typeof TimeLimit> | null>(null);
const lossLimitRef = ref<InstanceType<typeof LossLimit> | null>(null);
const proceedButtonRef = ref<InstanceType<typeof Button> | null>(null);

const antiRobotToken = computed<string>(
  () => proceedButtonRef.value?.antiRobotToken ?? '',
);

const isTimeLimitChangeBlocked = computed(() => {
  return timeLimitRef.value?.isTimeLimitChangeBlocked ?? false;
});

const isProceedDisabled = computed(() => {
  if (isLoading.value) return true;

  if (isCaptchaRegisterEnabled.value && !antiRobotToken.value) {
    return true;
  }

  return false;
});

const timeLimitPayload = computed(() => {
  const instance = timeLimitRef.value;

  if (!instance?.userLimitTime) {
    return null;
  }

  const userLimitTime = instance?.userLimitTime as UserLimitTime;
  const isInactive = userLimitTime.active === 0;

  return {
    user_limit_time_active: userLimitTime.active,
    ...(isInactive
      ? {}
      : {
          user_limit_time_period: Number(userLimitTime.period),
          user_limit_time: instance.xsdDuration as string,
        }),
  };
});

const lossLimitPayload = computed(() => {
  const instance = lossLimitRef.value;

  if (!instance?.userLimitLoss) {
    return null;
  }

  const userLostLimit = instance.userLimitLoss as UserLimit;
  const isInactive = userLostLimit.active === 0;

  return {
    user_limit_loss_active: userLostLimit.active,
    ...(isInactive
      ? {}
      : {
          user_limit_loss_period: Number(userLostLimit.period),
          user_limit_loss: Number(userLostLimit.amount),
        }),
  };
});

const formIsValid = computed(() => {
  if (!timeLimitPayload.value || !lossLimitPayload.value) {
    return false;
  }

  const timeLimitActive = timeLimitPayload.value.user_limit_time_active === 1;
  const lossLimitActive = lossLimitPayload.value.user_limit_loss_active === 1;

  if (!timeLimitActive && !lossLimitActive) {
    return true;
  }

  const isTimeLimitValid =
    timeLimitPayload.value.user_limit_time_period &&
    timeLimitPayload.value.user_limit_time?.trim();

  const isLossLimitValid =
    lossLimitPayload.value.user_limit_loss_period &&
    lossLimitPayload.value.user_limit_loss &&
    lossLimitPayload.value.user_limit_loss > 0;

  if (timeLimitActive && lossLimitActive) {
    return !!(isTimeLimitValid && isLossLimitValid);
  }

  return timeLimitActive ? !!isTimeLimitValid : !!isLossLimitValid;
});

function handleSubmit() {
  if (isProceedDisabled.value) {
    return;
  }

  if (!timeLimitPayload.value || !lossLimitPayload.value) {
    openToaster({
      message: t('account.account_protection.generic_error'),
      type: 'error',
    });

    return;
  }

  const payload: UserPendingLimits = {
    user_limits: {},
  };

  if (!isTimeLimitChangeBlocked.value) {
    payload.user_limits.time = {
      ...timeLimitPayload.value,
    };
  }

  payload.user_limits.loss = {
    ...lossLimitPayload.value,
  };

  handleAccountLimits(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.account_protection.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.account_protection.generic_error');

      openToaster({
        message,
        type: 'error',
      });
    });
}

async function handleAccountLimits(payload: UserPendingLimits) {
  const { ApiService } = useApiService();

  isLoading.value = true;

  return await ApiService.patch('/bff/users/update-pending-data', payload)
    .then(async (res: any) => {
      await fetchUserProfile({ force: true });
      await $containment.solve({
        name: 'user_limits',
        customValidator: () => res.data.success,
      });

      return res;
    })
    .catch((error: any) => {
      console.error({ error });

      throw error;
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>

<template>
  <AuthVerificationBox
    :description="$t('account.account_limits_section.description')"
    :is-loading="isLoading"
  >
    <div class="flex flex-col gap-1 -mt-6">
      <section :class="$style.sectionContainer">
        <span
          :class="$style.label"
          v-html="$t('account.account_limits_section.time.title')"
        />
        <UserAccountProtectionTimeLimit
          ref="timeLimitRef"
          hide-label
          hide-submit
          is-modal
        />
      </section>

      <section :class="$style.sectionContainer">
        <span
          :class="$style.label"
          v-html="$t('account.account_limits_section.loss.title')"
        />
        <UserAccountProtectionLostLimit
          ref="lossLimitRef"
          hide-label
          hide-submit
        />
      </section>

      <AuthVerificationBoxProceed
        ref="proceedButtonRef"
        class="mt-4"
        :disabled="isProceedDisabled || !formIsValid"
        @submit="handleSubmit"
      />
    </div>
  </AuthVerificationBox>
</template>

<style module lang="scss">
.sectionContainer {
  @apply mt-6;
}
.label {
  @apply flex flex-col justify-start text-2xs md:text-xs text-texts/70  mb-2 gap-1;
}
</style>
